/**
 * Derived-data selectors: virtual ports, edges, layout, and fan-out groups.
 * These are pure functions over `DefinitionState` so the UI can re-derive
 * whenever the state changes without bookkeeping.
 */

import dagre from 'dagre';

import { getPrimitive, PRIMITIVE_BY_KEY } from '~/pages/workflows/builder/fixtures/registry';
import type {
  DefinitionNode,
  DefinitionState,
  InputFieldDescriptor,
  OutputFieldDescriptor,
  PrimitiveDefinition,
  TaskDefinitionKey,
  ValueType,
  WorkflowDefinitionInputSlot
} from '~/pages/workflows/builder/types';

// ----- Effective schemas (with virtual ports) -----

export interface EntitySpec {
  kind: 'builtin' | 'custom';
  outputKey: string;
  label: string;
  prompt?: string;
  schema?: string;
}

/**
 * Returns the run-mode for an output node. Non-output nodes always return
 * `'batch'` as a harmless default; callers should gate on `primitive.category`
 * before reading this. `'batch'` means "consume the whole upstream list and
 * emit a single artifact"; `'per-item'` means "iterate the list and emit one
 * artifact per item" — the node then acts as a fan-out continuation.
 */
export function outputRunMode(node: DefinitionNode): 'batch' | 'per-item' {
  const mode = node.config.runMode;
  return mode === 'per-item' ? 'per-item' : 'batch';
}

export function isOutputNode(node: DefinitionNode): boolean {
  return getPrimitive(node.taskKey).category === 'output';
}

export function effectiveOutputs(node: DefinitionNode): OutputFieldDescriptor[] {
  const primitive = getPrimitive(node.taskKey);
  const base = primitive.outputSchema;

  let outputs: OutputFieldDescriptor[];

  if (node.taskKey === 'EXTRACT') {
    const entities = (node.config.entities as EntitySpec[] | undefined) ?? [];
    const virtual: OutputFieldDescriptor[] = entities.map((e) => ({
      key: e.outputKey,
      label: e.label,
      description:
        e.kind === 'builtin' ? `Built-in extraction: ${e.label}` : `Custom extraction: ${e.label}`,
      valueType: e.kind === 'builtin' ? (extractBuiltinValueType(e.outputKey) ?? 'json') : 'json',
      isList: true,
      correlatesWithInput: 'documents'
    }));
    outputs = [...base, ...virtual];
  } else if (node.taskKey === 'OUTPUT_INTEGRATION') {
    const actions =
      (node.config.actions as { capabilityKey: string; label?: string }[] | undefined) ?? [];
    const virtual: OutputFieldDescriptor[] = actions.map((a, i) => ({
      key: `action_${i}_${a.capabilityKey}`,
      label: a.label ?? a.capabilityKey,
      description: `Result of ${a.capabilityKey}`,
      valueType: 'record',
      isList: false
    }));
    outputs = [...base, ...virtual];
  } else {
    outputs = base;
  }

  // Output nodes in per-item mode emit N artifacts (one per upstream item),
  // so every output (including the virtual action result ports) becomes a
  // list when seen from downstream. In batch mode the schema is correct as-is.
  if (isOutputNode(node) && outputRunMode(node) === 'per-item') {
    outputs = outputs.map((o) => (o.isList ? o : { ...o, isList: true }));
  }

  return outputs;
}

function extractBuiltinValueType(_key: string): ValueType {
  // All extraction outputs are records — the domain-specific aliases
  // (driver/vehicle/location) are collapsed to the canonical 'record' kind.
  return 'record';
}

export function effectiveInputs(node: DefinitionNode): InputFieldDescriptor[] {
  const primitive = getPrimitive(node.taskKey);
  const schema = primitive.inputSchema;

  // Output nodes in batch mode explicitly take a list as their data input.
  // Flipping `data.isList` to true here lets the BindingPicker, the
  // `bestOutputMatch` auto-wire, and the validator (`list-scalar-mismatch`)
  // all treat list-to-list as the right wiring — no Aggregate step required.
  if (isOutputNode(node) && outputRunMode(node) === 'batch') {
    return schema.map((f) =>
      isInputLike(f.fieldKind) && f.key === 'data' ? { ...f, isList: true } : f
    );
  }

  return schema;
}

// ----- Edges -----

export interface Edge {
  id: string;
  sourceNodeId: string | null; // null => workflow input
  sourceOutputKey: string | null;
  sourceWorkflowInputKey?: string;
  targetNodeId: string;
  targetInputKey: string;
  forEach?: boolean;
}

export function computeEdges(state: DefinitionState): Edge[] {
  const edges: Edge[] = [];
  for (const node of state.nodes) {
    for (const binding of node.bindings) {
      if (binding.source.type === 'TASK_OUTPUT') {
        edges.push({
          id: `e_${binding.source.sourceNodeId}:${binding.source.sourceOutputKey}-${node.id}:${binding.inputKey}`,
          sourceNodeId: binding.source.sourceNodeId,
          sourceOutputKey: binding.source.sourceOutputKey,
          targetNodeId: node.id,
          targetInputKey: binding.inputKey
        });
      } else if (binding.source.type === 'WORKFLOW_INPUT') {
        edges.push({
          id: `e_input:${binding.source.workflowInputKey}-${node.id}:${binding.inputKey}`,
          sourceNodeId: null,
          sourceOutputKey: null,
          sourceWorkflowInputKey: binding.source.workflowInputKey,
          targetNodeId: node.id,
          targetInputKey: binding.inputKey
        });
      }
    }
    if (node.forEach) {
      if (node.forEach.kind === 'TASK_OUTPUT') {
        edges.push({
          id: `e_foreach_${node.forEach.sourceNodeId}-${node.id}`,
          sourceNodeId: node.forEach.sourceNodeId,
          sourceOutputKey: node.forEach.sourceOutputKey,
          targetNodeId: node.id,
          targetInputKey: node.forEach.inputKey,
          forEach: true
        });
      }
    }
  }
  return edges;
}

// ----- Fan-out groups -----

export interface FanOutGroup {
  id: string;
  sourceNodeId: string | null;
  sourceOutputKey: string | null;
  sourceLabel: string;
  memberNodeIds: string[];
}

/**
 * Groups nodes that share the same enumerable source. Any node with a
 * `forEach` pointer — or whose upstream chain all share that same pointer —
 * belongs to the same group, which is visualized as a dotted container.
 */
export function computeFanOutGroups(state: DefinitionState): FanOutGroup[] {
  const groups = new Map<string, FanOutGroup>();

  function keyForSource(nodeId: string | null, outputKey: string | null): string {
    return `${nodeId ?? 'workflow'}::${outputKey ?? 'input'}`;
  }

  const memberToGroup = new Map<string, string>();

  // First pass: every node with a forEach is a root member
  for (const node of state.nodes) {
    const fe = node.forEach;
    if (!fe) continue;
    let src: { nodeId: string | null; outputKey: string | null; label: string };
    if (fe.kind === 'TASK_OUTPUT') {
      const srcNode = state.nodes.find((n) => n.id === fe.sourceNodeId);
      const outputs = srcNode ? effectiveOutputs(srcNode) : [];
      const out = outputs.find((o) => o.key === fe.sourceOutputKey);
      src = {
        nodeId: fe.sourceNodeId,
        outputKey: fe.sourceOutputKey,
        label: `${srcNode?.name ?? srcNode?.taskKey ?? 'step'} · ${out?.label ?? fe.sourceOutputKey}`
      };
    } else {
      const slot = state.inputs.find((s) => s.key === fe.sourceWorkflowInputKey);
      src = {
        nodeId: null,
        outputKey: fe.sourceWorkflowInputKey,
        label: slot?.label ?? 'input'
      };
    }
    const gkey = keyForSource(src.nodeId, src.outputKey);
    if (!groups.has(gkey)) {
      groups.set(gkey, {
        id: gkey,
        sourceNodeId: src.nodeId,
        sourceOutputKey: src.outputKey,
        sourceLabel: src.label,
        memberNodeIds: []
      });
    }
    groups.get(gkey)!.memberNodeIds.push(node.id);
    memberToGroup.set(node.id, gkey);
  }

  // Second pass: propagate membership forward — any node whose DATA bindings
  // all come from a single group joins that group (correlated fan-in).
  let changed = true;
  let safety = 0;
  while (changed && safety < 20) {
    changed = false;
    safety += 1;
    for (const node of state.nodes) {
      if (memberToGroup.has(node.id)) continue;
      const dataBindings = node.bindings.filter((b) => {
        const field = getPrimitive(node.taskKey).inputSchema.find((f) => f.key === b.inputKey);
        return field ? isInputLike(field.fieldKind) : false;
      });
      if (dataBindings.length === 0) continue;
      const parentGroups = new Set<string>();
      let externalSource = false;
      for (const b of dataBindings) {
        if (b.source.type !== 'TASK_OUTPUT') {
          externalSource = true;
          break;
        }
        const g = memberToGroup.get(b.source.sourceNodeId);
        if (g) parentGroups.add(g);
        else externalSource = true;
      }
      if (externalSource || parentGroups.size === 0) continue;
      if (parentGroups.size !== 1) continue; // multi-group parents => aggregation boundary
      const onlyGroup = [...parentGroups][0];
      if (!onlyGroup) continue;
      // AGGREGATE is an explicit fan-in boundary: it should NOT inherit the group.
      if (node.taskKey === 'AGGREGATE') continue;
      // Output nodes in "batch" run mode are also explicit fan-in boundaries —
      // they absorb the whole upstream list and produce a single artifact, so
      // they belong visually *after* the dotted container, not inside it. In
      // per-item mode they already get a `forEach` pointer and joined the group
      // via the first pass above.
      if (isOutputNode(node) && outputRunMode(node) === 'batch') continue;
      groups.get(onlyGroup)!.memberNodeIds.push(node.id);
      memberToGroup.set(node.id, onlyGroup);
      changed = true;
    }
  }

  return [...groups.values()];
}

export function groupForNode(state: DefinitionState, nodeId: string): FanOutGroup | null {
  const groups = computeFanOutGroups(state);
  return groups.find((g) => g.memberNodeIds.includes(nodeId)) ?? null;
}

// ----- Layout (dagre) -----

export interface LaidOutPosition {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

const NODE_WIDTH = 280;
// Fallback height used when we don't have a concrete estimate. This is kept
// somewhat generous because dagre will only separate nodes by what we tell it,
// and undersized boxes cause parallel siblings to visually overlap.
const NODE_HEIGHT = 160;
// Vertical gap between parallel siblings.
const NODE_SEP = 56;
// Horizontal gap between ranks.
const RANK_SEP = 120;

/**
 * Estimate the rendered height of a TaskNode based on its content. This lets
 * dagre reserve enough vertical space between parallel (same-rank) nodes so
 * they don't overlap. The numbers mirror the Tailwind spacing used in
 * `canvas/task-node.tsx`; when that file changes, re-tune these.
 */
export function estimateNodeHeight(node: DefinitionNode): number {
  const primitive = getPrimitive(node.taskKey);
  const outputs = effectiveOutputs(node);
  const isUnconfiguredInput = primitive.category === 'input' && !node.inputSubtype;

  // Header row: icon + label + uppercase shortLabel.
  let h = 56;

  // Body.
  if (isUnconfiguredInput) {
    // Polymorphic picker renders a 2-column grid of all input subtypes.
    const rows = Math.ceil(6 / 2);
    h += 16 /* "What kind of input?" hint */ + rows * 36 /* buttons + gap */ + 8;
  } else {
    // Summary paragraph (clamped to 2 lines) or a single-line hint.
    h += 44;
  }

  // Badges row — "Workflow input", "Per-item", "Combines list", output chips.
  h += 30;

  // Dynamic output panel (only EXTRACT / OUTPUT_INTEGRATION render it).
  if ((node.taskKey === 'EXTRACT' || node.taskKey === 'OUTPUT_INTEGRATION') && outputs.length > 0) {
    const chipsPerRow = 3;
    const rows = Math.max(1, Math.ceil(outputs.length / chipsPerRow));
    h += 12 /* border + top padding */ + 18 /* "Outputs" header */ + rows * 24 + 8;
  }

  return Math.max(NODE_HEIGHT, h);
}

export function computeLayout(
  state: DefinitionState,
  opts: { direction?: 'LR' | 'TB' } = {}
): Map<string, { x: number; y: number }> {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: opts.direction ?? 'LR',
    nodesep: NODE_SEP,
    ranksep: RANK_SEP,
    // Tell dagre to treat node dimensions as tight bounding boxes; nodesep
    // then reflects the actual visual gap between siblings.
    marginx: 16,
    marginy: 16
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const node of state.nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: estimateNodeHeight(node) });
  }

  const edges = computeEdges(state);
  for (const edge of edges) {
    if (edge.sourceNodeId) g.setEdge(edge.sourceNodeId, edge.targetNodeId);
  }

  dagre.layout(g);
  const positions = new Map<string, { x: number; y: number }>();
  // Layout is fully owned by dagre — the `layout` field on DefinitionNode is
  // intentionally ignored so adding/removing/rewiring a node always re-lays out
  // the whole graph deterministically.
  for (const node of state.nodes) {
    const laid = g.node(node.id);
    if (laid) {
      positions.set(node.id, { x: laid.x - laid.width / 2, y: laid.y - laid.height / 2 });
    } else {
      positions.set(node.id, { x: 0, y: 0 });
    }
  }
  return positions;
}

export { NODE_WIDTH, NODE_HEIGHT };

// ----- Compatibility -----

/**
 * Returns true if a field's `fieldKind` represents a runtime-resolved input
 * (as opposed to a CONFIG field resolved from the node's config blob).
 * Both the canonical `INPUT` kind and the legacy `DATA`/`FILE` aliases are
 * treated as input-like.
 */
export function isInputLike(kind: import('~/pages/workflows/builder/types').FieldKind): boolean {
  return kind === 'INPUT' || kind === 'DATA' || kind === 'FILE';
}

const TYPE_HIERARCHY: Record<ValueType, ValueType[]> = {
  any: [],
  text: ['any'],
  number: ['any'],
  bool: ['any'],
  file: ['any'],
  json: ['any'],
  record: ['json', 'any'],
  list: ['any']
};

export function isAssignable(from: ValueType, to: ValueType): boolean {
  if (from === to) return true;
  if (to === 'any') return true;
  if (from === 'any') return true;
  return (TYPE_HIERARCHY[from] ?? []).includes(to);
}

/**
 * Returns true if a source value of `from` kind can be bound to `field`.
 * When `field.acceptedKinds` is present and non-empty, only sources whose kind
 * is in that list (or 'any') are compatible. Falls back to `field.valueType`
 * for fields without an explicit acceptedKinds constraint.
 */
export function isAssignableToField(from: ValueType, field: InputFieldDescriptor): boolean {
  if (field.acceptedKinds && field.acceptedKinds.length > 0) {
    return field.acceptedKinds.some((k) => isAssignable(from, k));
  }
  return isAssignable(from, field.valueType);
}

export interface BindingCandidate {
  kind: 'slot' | 'output';
  label: string;
  description?: string;
  valueType: ValueType;
  isList: boolean;
  sourceNodeId?: string;
  sourceOutputKey?: string;
  sourceWorkflowInputKey?: string;
  slotKey?: string;
}

/**
 * Lists every source a user could pick for a DATA field. Does NOT filter by
 * compatibility — the picker renders all but dims incompatible ones so the
 * user can see what's available.
 */
export function listBindingCandidates(
  state: DefinitionState,
  targetNodeId: string
): { slots: BindingCandidate[]; outputs: BindingCandidate[] } {
  const slotCandidates: BindingCandidate[] = state.inputs.map((slot) => ({
    kind: 'slot',
    label: slot.label,
    description: `Workflow input · ${slot.kind.toLowerCase()}${slot.enumerable ? ' · list' : ''}`,
    valueType: slotValueType(slot),
    isList: slot.enumerable,
    sourceWorkflowInputKey: slot.key,
    slotKey: slot.key
  }));

  const orderedNodes = upstreamNodes(state, targetNodeId);
  const outputCandidates: BindingCandidate[] = [];
  for (const node of orderedNodes) {
    const outs = effectiveOutputs(node);
    for (const out of outs) {
      outputCandidates.push({
        kind: 'output',
        label: `${node.name ?? PRIMITIVE_BY_KEY[node.taskKey]?.label ?? node.taskKey} · ${out.label}`,
        description: out.description,
        valueType: out.valueType,
        isList: out.isList,
        sourceNodeId: node.id,
        sourceOutputKey: out.key
      });
    }
  }
  return { slots: slotCandidates, outputs: outputCandidates };
}

function slotValueType(slot: WorkflowDefinitionInputSlot): ValueType {
  switch (slot.kind) {
    case 'FILE':
      return 'file';
    case 'TEXT':
      return 'text';
    case 'JSON':
      return 'json';
    case 'AMS_SELECTION':
      return 'record';
    default:
      return 'any';
  }
}

/**
 * Returns all nodes that are (transitively) upstream of `targetNodeId`,
 * excluding `targetNodeId` itself. Topologically sorted.
 */
export function upstreamNodes(state: DefinitionState, targetNodeId: string): DefinitionNode[] {
  const byId = new Map(state.nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const order: DefinitionNode[] = [];
  function walk(id: string) {
    const node = byId.get(id);
    if (!node) return;
    for (const b of node.bindings) {
      if (b.source.type === 'TASK_OUTPUT' && !visited.has(b.source.sourceNodeId)) {
        visited.add(b.source.sourceNodeId);
        walk(b.source.sourceNodeId);
        const upstream = byId.get(b.source.sourceNodeId);
        if (upstream) order.push(upstream);
      }
    }
    if (node.forEach?.kind === 'TASK_OUTPUT' && !visited.has(node.forEach.sourceNodeId)) {
      visited.add(node.forEach.sourceNodeId);
      walk(node.forEach.sourceNodeId);
      const upstream = byId.get(node.forEach.sourceNodeId);
      if (upstream) order.push(upstream);
    }
  }
  walk(targetNodeId);

  // Fallback: if the target isn't bound yet, still show every other node as a
  // candidate so the picker is useful.
  if (order.length === 0) {
    return state.nodes.filter((n) => n.id !== targetNodeId);
  }
  return order;
}

export function isEnumerableNode(state: DefinitionState, node: DefinitionNode): boolean {
  if (node.forEach) return true;
  const group = groupForNode(state, node.id);
  return Boolean(group);
}

/**
 * Returns true if any DATA binding on `node` points at an upstream output (or
 * workflow input slot) whose value is a list. Used by the output-node run-mode
 * UI to decide whether the mode toggle actually matters (it only changes
 * behavior when there's something to fan over).
 */
export function hasUpstreamList(state: DefinitionState, node: DefinitionNode): boolean {
  const primitive = getPrimitive(node.taskKey);
  for (const b of node.bindings) {
    const field = primitive.inputSchema.find((f) => f.key === b.inputKey);
    if (!field || !isInputLike(field.fieldKind)) continue;
    const src = b.source;
    if (src.type === 'TASK_OUTPUT') {
      const source = state.nodes.find((n) => n.id === src.sourceNodeId);
      if (!source) continue;
      const out = effectiveOutputs(source).find((o) => o.key === src.sourceOutputKey);
      if (out?.isList) return true;
    } else if (src.type === 'WORKFLOW_INPUT') {
      const slot = state.inputs.find((s) => s.key === src.workflowInputKey);
      if (slot?.enumerable) return true;
    }
  }
  return false;
}

/**
 * Returns a `ForEach` pointer at the first list-typed DATA binding on `node`,
 * or null when none exist. Used to auto-populate `node.forEach` when the user
 * switches an output node into per-item mode.
 */
export function firstEnumerableBinding(
  state: DefinitionState,
  node: DefinitionNode
): import('~/pages/workflows/builder/types').ForEach | null {
  const primitive = getPrimitive(node.taskKey);
  for (const b of node.bindings) {
    const field = primitive.inputSchema.find((f) => f.key === b.inputKey);
    if (!field || !isInputLike(field.fieldKind)) continue;
    const src = b.source;
    if (src.type === 'TASK_OUTPUT') {
      const source = state.nodes.find((n) => n.id === src.sourceNodeId);
      if (!source) continue;
      const out = effectiveOutputs(source).find((o) => o.key === src.sourceOutputKey);
      if (out?.isList) {
        return {
          kind: 'TASK_OUTPUT',
          sourceNodeId: src.sourceNodeId,
          sourceOutputKey: src.sourceOutputKey,
          inputKey: b.inputKey
        };
      }
    } else if (src.type === 'WORKFLOW_INPUT') {
      const slot = state.inputs.find((s) => s.key === src.workflowInputKey);
      if (slot?.enumerable) {
        return {
          kind: 'WORKFLOW_INPUT',
          sourceWorkflowInputKey: slot.key,
          inputKey: b.inputKey
        };
      }
    }
  }
  return null;
}

/**
 * Returns the DATA field from `node`'s effective input schema most likely to
 * be the "primary" data input — used by the auto-wire logic when inserting a
 * step. Preference: first required DATA field, then first DATA field.
 */
export function primaryDataInput(taskKey: TaskDefinitionKey): InputFieldDescriptor | null {
  const schema = PRIMITIVE_BY_KEY[taskKey]?.inputSchema ?? [];
  return (
    schema.find((f) => isInputLike(f.fieldKind) && f.required) ??
    schema.find((f) => isInputLike(f.fieldKind)) ??
    null
  );
}

/**
 * Pick the best output on a given source node for binding into a given target
 * input field. Preference: exact valueType match + list-flag match.
 */
export function bestOutputMatch(
  sourceNode: DefinitionNode,
  targetField: InputFieldDescriptor
): OutputFieldDescriptor | null {
  const outs = effectiveOutputs(sourceNode);
  const strict = outs.find(
    (o) => isAssignableToField(o.valueType, targetField) && o.isList === targetField.isList
  );
  if (strict) return strict;
  const loose = outs.find((o) => isAssignableToField(o.valueType, targetField));
  if (loose) return loose;
  return outs[0] ?? null;
}

export function findOutput(
  state: DefinitionState,
  nodeId: string,
  outputKey: string
): { node: DefinitionNode; output: OutputFieldDescriptor } | null {
  const node = state.nodes.find((n) => n.id === nodeId);
  if (!node) return null;
  const output = effectiveOutputs(node).find((o) => o.key === outputKey);
  if (!output) return null;
  return { node, output };
}

export function primitiveForNode(node: DefinitionNode): PrimitiveDefinition {
  return getPrimitive(node.taskKey);
}
